import React, { useState, useLayoutEffect, useEffect, useRef, useCallback } from 'react';
import App from './App';
import usePendo from '../hooks/usePendo';
import useEvents from '../hooks/useEvents';
import axios from 'axios';
import getHeaders from '../utils/getHeaders';
import { $_GET, fetchAndCacheState, getToken, pauseLiveEvent, playLiveEvent, setToken } from '../Upshow';
import UpshowLogger from '../Logger';
import Lottie from '@upshow/lottie';
import everPassFlagAnimation from '../assets/animations/EverPass_Flag_Wave.json';
import styles from './App.module.scss';
import { getMenuData } from '../utils/getMenuData';
import useOutsideClick from '../components/OutsideClickHandler';
import highShadow from '../assets/highShadow.svg'; // background shadow
import { EventStatusDictionary } from '../helpers/eventHelper';
import { isXumoDevice } from '../utils/isXumoDevice';

const PARENT_ID = '6198006499835904';
const blockKeys = new Set(['Backspace', 'Escape', 'ArrowDown', 'BrowserBack', 'Tab']);

export default function AppWrapper({ epsOnly = false }) {
    const [uiStarted, setUiStarted] = useState(false);
    const [appState, setAppState] = useState(() => window.__UpshowState);
    const [mounted, setMounted] = useState(false); // render <App>?
    const [menuOpen, setMenuOpen] = useState(false); // prop for <App>
    const [pollingPaused, setPollingPaused] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubscribeLoading, setIsSubscribeLoading] = useState(false);
    const eventsApi = useEvents(pollingPaused);
    const BASE_URL = process.env.REACT_APP_REST_URL;
    const [sportsData, setSportsData] = useState([]);
    const [shortcutsData, setShortcutsData] = useState([]);
    const containerRef = useRef(null);
    const [subscribedEventInfo, setSubscribedEventInfo] = useState(null);
    const [searchOpen, setSearchOpen] = useState(false);

    const [afterLoading, setAfterLoading] = useState(false);

    const isXumo = isXumoDevice();

    const activeLiveEventId = window?.__UpshowState?.activeLiveEvent?.id ?? appState?.activeLiveEvent?.id;
    const featureOn = !!appState?.settings.features.on_screen_menu;

    if(isXumo) document.body.classList.add('is-xumo');

    const closeMenu = useCallback(() => {
        if (!menuOpen && !mounted) return;

        if(isXumo) {
            setMenuOpen(false);
            setMounted(false);
            return;
        }
        requestAnimationFrame(() => {
            setMenuOpen(false);
        });

    }, [menuOpen, mounted]);

    useEffect(() => {
        if(isXumo && !mounted) {
            if(!mounted) {
                playLiveEvent();
            } else {
                pauseLiveEvent();
            }
        }
    }, [mounted])

    useEffect(() => {
        if(!getToken()) setToken($_GET['api_token']);
    }, []);

    const openMenu = useCallback(() => {
        if (menuOpen) return;
        setMounted(true);
        if(isXumo) {
            setMenuOpen(true);
            return;
        }
        requestAnimationFrame(() => {
            setMenuOpen(true);
        });
    }, [menuOpen]);

    // Handle outside click to open menu when closed or close it when open
    useOutsideClick(containerRef, openMenu, uiStarted, !menuOpen && !searchOpen);
    useOutsideClick(containerRef, closeMenu, uiStarted, menuOpen && !searchOpen);
    useEffect(() => {
      if (epsOnly) {
        document.body.style.background = "transparent";
        document.body.style.backgroundColor = "transparent";
      } else {
        document.body.style.background = "#000000";
      }
      return () => {
        document.body.style.background = "#000000";
      };
    }, [epsOnly]);

    const handleMarketingClick = async () => {
        setSubscribedEventInfo(null);

        if (!activeLiveEventId) return;

        setIsLoading(true);
        try {
            await axios.post(`${BASE_URL}/v6/unsubscribe/${eventId}`, {}, { headers: getHeaders() });
            await fetchAndCacheState();
        } catch (err) {
            UpshowLogger.error(['unsubscribe'], err);
        } finally {
            setIsLoading(false);
            setAfterLoading(true);
            closeMenu();
        }
    };

    // Toggle
    const handleToggleSubscription = async (event) => {
        setIsSubscribeLoading(true);
        try {

            if (event.isDeviceSubscribed && event.status !== EventStatusDictionary.LIVE) {
                await axios.post(`${BASE_URL}/v6/unsubscribe/${event.id}`, {}, { headers: getHeaders() });
            } else {
                await axios.post(`${BASE_URL}/v6/subscribe/${event.id}`, {}, { headers: getHeaders() });
            }

            await eventsApi.fetchEvents();

            if (event.status === EventStatusDictionary.LIVE){
                closeMenu();
                await fetchAndCacheState();
            }
        } catch (err) {
            console.error('toggleSubscription error:', err);
            setAfterLoading(true);
        } finally {
            setIsSubscribeLoading(false);
        }
    };

    /* ------------------------------------------------------------------ */
    /* ❷  First-key listener → mount + slide-in                            */
    useEffect(() => {
        if(!uiStarted) return;
        const onKey = (e) => {
            const key = e.key ?? e.detail?.action ?? e.detail; // tvremote fallback
            if (key === 'Tab' && !mounted) {
                e.preventDefault();
                return;
            }
            if (!blockKeys.has(key)) {
                if(isXumo) {
                    pauseLiveEvent();
                }
                setMounted(true);
                requestAnimationFrame(() => setMenuOpen(true));
                setPollingPaused(false);
            }
        };


        document.addEventListener('keydown', onKey, true);
        document.addEventListener('tvremote', onKey, true);
        return () => {
            document.removeEventListener('keydown', onKey, true);
            document.removeEventListener('tvremote', onKey, true);
        };
    }, [mounted, uiStarted]);

    useEffect(() => {
        setUiStarted(featureOn);
    }, [featureOn])

    useEffect(() => {
        const onUpdate = (e) => setAppState(e.detail);
        window.addEventListener('UpshowStateUpdate', onUpdate);
        return () => window.removeEventListener('UpshowStateUpdate', onUpdate);
    }, []);

    /* ------------------------------------------------------------------ */
    /* ❹  Pendo                                                           */
    usePendo({
        enabled: uiStarted && featureOn && !epsOnly, // Disable Pendo when epsOnly is true
        visitorId: appState?.settings?.device_id,
        accountId: appState?.settings?.organization_id,
        parentId: PARENT_ID,
    });

    useEffect(() => {
        fetchAndCacheState();
    }, []);

    useEffect(() => {
        if(sportsData.length && shortcutsData.length) return;
        getMenuData(eventsApi.completeLiveEvents).then((data) => {
            setSportsData(data.sports || []);
            setShortcutsData(data.shortcuts || []);
        });
    }, [eventsApi.completeLiveEvents]);

    // Preload league images to avoid hover delay
    useEffect(() => {
        if (!shortcutsData || shortcutsData.length === 0) return;
        const images = [];
        shortcutsData.forEach((league) => {
            if (league.defaultLogo) {
                const img = new window.Image();
                img.src = league.defaultLogo;
                images.push(img);
            }
            if (league.logo) {
                const img = new window.Image();
                img.src = league.logo;
                images.push(img);
            }
        });
    }, [shortcutsData]);

    if (afterLoading && epsOnly) {
        return (
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: '#000',
                    zIndex: 0,
                }}
            />);
    }

    return (
        <>
            {isLoading && uiStarted && (
                <div className={styles.loadingEvent}>
                    { !isXumo ? <Lottie
                        options={{
                            loop: true,
                            autoplay: true,
                            animationData: everPassFlagAnimation,
                        }}
                    />:<img src='https://cdn.upshow.tv/ui-assets/Xumo_Loading_Screen.jpg'  alt="Loading" className={styles.loadingImg}/> }
                </div>
            )}

            {subscribedEventInfo && !menuOpen && !isLoading && window?.__UpshowState?.activeLiveEvent?.id && (
                <div className={styles.subscribeOverlay}>
                    <img src={highShadow} alt="Shadow" className={styles.shadowImage} />
                    <div className={styles.overlayContent}>
                        <h3>{subscribedEventInfo.title}</h3>
                        <p>{subscribedEventInfo.label}</p>
                    </div>
                </div>
            )}
            <App 
                appState={appState} 
                onRequestOpen={() => setMenuOpen(true)}
                onRequestClose={() => closeMenu()} 
                onHidden={() => setMounted(false)} 
                open={menuOpen} 
                setPollingPaused={setPollingPaused} 
                eventsApi={eventsApi} 
                epsOnly={epsOnly} 
                onToggleSubscription={handleToggleSubscription} 
                isLoading={isLoading}
                isSubscribeLoading={isSubscribeLoading}
                containerRef={containerRef} 
                sportsData={sportsData} 
                shortcutsData={shortcutsData} 
                mounted={mounted} 
                onShowSearchChange={setSearchOpen} 
                openMenu={openMenu} 
                handleMarketingClick={handleMarketingClick}
                isXumo={isXumo}
                activeLiveEventId={activeLiveEventId}
            />
        </>
    );
}
